import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useDashboard } from "@/hooks/use-dashboard";
import Header from "@/components/Header";
import TodaysAnalysisDashboard from "./analytics";
import ExampleLeadPipeline from "./lead-pipeline";
import RatingComponent from "./ratings";
import UpcomingAppointments from "./upcoming-appointments";
import { Calendar, Users, Star, TrendingUp, ChevronRight } from "lucide-react";

const QuickLink = ({ to, icon: Icon, label, count }) => (
  <Link
    to={to}
    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
  >
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <span className="text-sm font-medium text-gray-700">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      {count !== undefined && (
        <span className="text-sm text-gray-500">{count}</span>
      )}
      <ChevronRight className="h-4 w-4 text-gray-400" />
    </div>
  </Link>
);

export default function DashboardPage() {
  const { astrologer } = useAuth();
  const { stats, loading: statsLoading } = useDashboard('today');

  return (
    <>
      <Header name="Dashboard" />
      <div className="w-full p-3">
        {/* Analytics Section */}
        <div className="bg-white rounded-lg shadow-sm mb-3">
          <TodaysAnalysisDashboard />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 flex flex-col gap-3">
            {/* Upcoming + Leads Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-white rounded-lg shadow-sm">
                <UpcomingAppointments 
                  appointments={stats?.upcomingAppointments || []} 
                  loading={statsLoading} 
                />
              </div>
              <div className="bg-white rounded-lg shadow-sm">
                <ExampleLeadPipeline maxLeads={4} />
              </div>
            </div>

            {/* Ratings */}
            <div className="bg-white rounded-lg shadow-sm">
              <RatingComponent astrologerId={astrologer?.astrologerId} compact />
            </div>
          </div>

          {/* Right Column - Quick Links */}
          <div className="lg:col-span-1 space-y-3">
            <div className="bg-white rounded-lg shadow-sm p-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Quick Access</h3>
              <div className="space-y-2">
                <QuickLink
                  to="/schedule/calendar"
                  icon={Calendar}
                  label="Schedule"
                  count={stats?.upcomingAppointments?.length || 0}
                />
                <QuickLink
                  to="/consultations/clients"
                  icon={Users}
                  label="Clients"
                />
                <QuickLink
                  to="/reviews"
                  icon={Star}
                  label="Reviews"
                />
                <QuickLink
                  to="/earnings"
                  icon={TrendingUp}
                  label="Earnings"
                />
              </div>
            </div>

            {/* Performance Tips */}
            <div className="bg-orange-50 rounded-lg p-3">
              <h3 className="text-sm font-semibold text-orange-800 mb-2">Tips</h3>
              <ul className="text-xs text-orange-700 space-y-1">
                <li>• Stay online during peak hours (6-10 PM)</li>
                <li>• Complete your profile for better visibility</li>
                <li>• Respond quickly to chat messages</li>
                <li>• Maintain a quiet environment for calls</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
